/**
 * Intelligence → Agents: the customer's door to the agents operating in
 * their own organization.
 *
 * Paths and shapes mirror `apps/api/src/domains/agent-runtime/routes.ts`
 * verbatim.
 *
 * NOT YET RE-EXPORTED FROM `index.ts`. That file is integrator-owned, and the
 * one line it needs is:
 *
 *     export * as agents from './agents.js';
 *
 * ---------------------------------------------------------------------------
 * THE ONE THING A CONSUMER OF THIS MODULE MUST NOT DO
 * ---------------------------------------------------------------------------
 * `AgentSummary.permissionProfile` is what was DELEGATED at deploy time. It is
 * NOT what the agent may do. Authority is decided at FIRE time, re-derived
 * from the live catalog at four boundaries per run, and a deployer who has
 * since lost one key refuses the whole run.
 *
 * So the two facts are separate fields on separate types — the profile on the
 * summary, the verdict on `AgentDetail.fireTimeAuthority` — and the list
 * response carries no verdict at all. A screen that renders a profile as a
 * capability list is showing the customer something untrue, and the shapes
 * here are arranged so that doing it takes a deliberate act.
 */
import { get, patch, post } from './transport.js';
/** `GET /api/agents` — needs `agents:read`. */
export async function list(options) {
    const body = await get('/api/agents', options);
    return body.agents;
}
/**
 * `GET /api/agents/:id` — needs `agents:read`.
 *
 * 404 for an agent that does not exist AND for one belonging to another
 * tenant, indistinguishably: telling them apart would be an existence oracle.
 */
export async function detail(deploymentId, options) {
    return get(`/api/agents/${encodeURIComponent(deploymentId)}`, options);
}
/** `GET /api/agents/:id/runs` — needs `agents:read`. */
export async function runs(deploymentId, query = {}, options) {
    const suffix = query.limit === undefined ? '' : `?limit=${query.limit}`;
    const body = await get(`/api/agents/${encodeURIComponent(deploymentId)}/runs${suffix}`, options);
    return body.runs;
}
/** `GET /api/agents/runs/:runId` — needs `agents:read`. */
export async function run(runId, options) {
    const body = await get(`/api/agents/runs/${encodeURIComponent(runId)}`, options);
    return body.run;
}
/** `GET /api/agents/:id/activity` — the canonical audit stream, this agent only. */
export async function activity(deploymentId, query = {}, options) {
    const parts = [
        query.limit === undefined ? '' : `limit=${query.limit}`,
        query.beforeAt ? `beforeAt=${encodeURIComponent(query.beforeAt)}` : '',
        query.beforeId ? `beforeId=${encodeURIComponent(query.beforeId)}` : '',
    ].filter((part) => part !== '');
    const suffix = parts.length > 0 ? `?${parts.join('&')}` : '';
    return get(`/api/agents/${encodeURIComponent(deploymentId)}/activity${suffix}`, options);
}
/** `GET /api/agents/approvals` — pending decisions. Needs `agents:read`. */
export async function approvals(options) {
    const body = await get('/api/agents/approvals', options);
    return body.approvals;
}
/** `POST /api/agents/:id/pause` — reversible. Needs `agents:pause`. */
export async function pause(deploymentId, options) {
    const body = await post(`/api/agents/${encodeURIComponent(deploymentId)}/pause`, {}, options);
    return body.agent;
}
/** `POST /api/agents/:id/resume` — PAUSED → ACTIVE. Needs `agents:pause`. */
export async function resume(deploymentId, options) {
    const body = await post(`/api/agents/${encodeURIComponent(deploymentId)}/resume`, {}, options);
    return body.agent;
}
/**
 * `POST /api/agents/:id/emergency-stop` — needs `agents:emergency_stop`, its
 * OWN key and never implied by `agents:pause`.
 *
 * Not reversible from this door: `DISABLED` has no outgoing transition except
 * back to `DRAFT`, which is a re-configuration, not a status flip.
 */
export async function emergencyStop(deploymentId, options) {
    return post(`/api/agents/${encodeURIComponent(deploymentId)}/emergency-stop`, {}, options);
}
/** `POST /api/agents/runs/:runId/cancel` — needs `agents:pause`. */
export async function cancelRun(runId, options) {
    const body = await post(`/api/agents/runs/${encodeURIComponent(runId)}/cancel`, {}, options);
    return body.run;
}
/**
 * `POST /api/agents/approvals/:id/decision` — needs `agents:approve`.
 *
 * A reason is required. A decision nobody can review later is not a decision.
 */
export async function decideApproval(approvalId, decision, reason, options) {
    const body = await post(`/api/agents/approvals/${encodeURIComponent(approvalId)}/decision`, { decision, reason }, options);
    return body.run;
}
/**
 * `GET /api/agents/catalog` — needs `agents:configure`.
 *
 * Deliberately NOT gated on enablement or consent: a prospective
 * administrator looking at what the product offers is not configuring
 * anything, and refusing the catalog would make the refusal unexplainable.
 */
export async function catalog(options) {
    return get('/api/agents/catalog', options);
}
/**
 * `GET /api/agents/consent` — needs `agents:read`, NOT the governance key.
 *
 * Seeing the decision and making it are different acts, and this product
 * splits them wherever an organization accepts something.
 */
export async function consent(options) {
    return get('/api/agents/consent', options);
}
/**
 * `POST /api/agents/consent` — needs `agents:configure` (carried by org_admin
 * since the owner directive of 2026-10-07). Consent is never inferred from
 * holding it: it exists only once a holder has recorded the decision here.
 *
 * THREE THINGS THIS FUNCTION CANNOT SEND, and each absence is load-bearing:
 * the deciding user (the session decides it, and the request schema is a
 * `strictObject` with no field for it), the instant (a database trigger), and
 * the ordinal (derived from the ledger). A caller-chosen version is a
 * caller-chosen history.
 *
 * `termsVersion` is REQUIRED and is an ECHO of the edition being agreed to —
 * the `knowledge_ack` pattern. The route refuses any other value with a 409
 * carrying the published edition. An optional parameter here would let a
 * client omit it and be given the current edition by default, which is a
 * stamp rather than an acknowledgement.
 */
export async function recordConsent(input, options) {
    return post('/api/agents/consent', input, options);
}
/**
 * `POST /api/agents` — needs `agents:deploy`. Creates the agent in DRAFT.
 *
 * DRAFT AND NOT ACTIVE, and the split is not ceremony: activation is
 * {@link activate}, a separate act with its own audit row. Neither the
 * deployer id nor the `tenant_user` stamp can be chosen here; both come from
 * the session.
 */
export async function deploy(input, options) {
    const body = await post('/api/agents', input, options);
    return body.agent;
}
/**
 * `PATCH /api/agents/:id/configuration` — needs `agents:configure`.
 *
 * PARTIAL, and absent means "leave it alone". A PUT would make omitting
 * `triggers` a request to clear them — a destructive edit a client produces by
 * forgetting.
 *
 * REFUSED WHILE THE DEPLOYMENT IS ACTIVE (409-shaped lifecycle error), because
 * a run in flight holds a principal built from the profile this call would
 * replace. Pause it, change it, resume it: three acts, three audit rows, and
 * nobody guessing which configuration a run was operating under.
 */
export async function configure(deploymentId, input, options) {
    const body = await patch(`/api/agents/${encodeURIComponent(deploymentId)}/configuration`, input, options);
    return body.agent;
}
/**
 * `POST /api/agents/:id/activate` — needs `agents:deploy`, not
 * `agents:configure`: this is the act the deploy key's own catalog description
 * names, "put an operational agent into this organization AND TURN IT ON".
 *
 * Walks DRAFT -> CONFIGURED -> DEPLOYED -> ACTIVE, so it throws on a PAUSED
 * row; resuming a paused agent is {@link resume}, which is `agents:pause`
 * because a resume is the other half of a pause.
 */
export async function activate(deploymentId, options) {
    const body = await post(`/api/agents/${encodeURIComponent(deploymentId)}/activate`, {}, options);
    return body.agent;
}
//# sourceMappingURL=agents.js.map